import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CloseIcon,
  GKDataGrid,
  Grid2,
  IconButton,
  KebabMenu2Icon,
  LinkIcon,
  NoData,
  Popover,
  Show,
  StatusField,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { moneyCommaSeparator } from "utils";
import axiosInstance from "utils/axiosInstance";
import {
  copyTextToClipboard,
  extraColumnsField,
  matchDate,
} from "utils/helpers";

const PaymentLinkModal = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [linkDetails, setLinkDetails] = useState(null);

  const requestBody = useGenerateQueryParams("paymentLink");

  const { data, isFetching, refetch } = useFetchQuery({
    key: ["PAYMENT_LINK_LIST", requestBody],
    route: `/api/revenue/payment-links/`,
    requestBody,
  });

  const cancelPaymentLink = useMutation({
    mutationKey: ["CANCEL_PAYMENT_LINK"],
    mutationFn: (rowId: string) =>
      axiosInstance.patch(`/api/revenue/payment-links/${rowId}/cancel/`),
    onSuccess: () => {
      refetch();
    },
  });

  const handleCopyLink = async (linkId: string) => {
    const success = await copyTextToClipboard(linkId);
    if (success) {
      toast.success("Link copied to clipboard!");
    } else {
      toast.error("Failed to copy link");
    }
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio (${data?.data?.totalCount})`,
        "clientName",
        230,
        1,
      ),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography
              fontWeight={700}
              maxWidth={"100%"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Invoice No.", "invoiceNumber", 150, 1),
      valueGetter: (row) => row?.row?.invoiceNumber || "-",
    },
    {
      ...extraColumnsField("Amount (₹)", "linkAmount", 150, 1),
      type: "number",
      valueGetter: (row) => moneyCommaSeparator(row?.row?.linkAmount),
    },
    {
      ...extraColumnsField("Created Date", "createdAt", 200, 1),
      type: "date",
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField("Link Status", "linkStatus", 150, 1),
      renderCell: (params) => {
        return <StatusField statusString={params?.row?.linkStatus} />;
      },
    },
    {
      ...extraColumnsField("Action", "action", 100, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <IconButton
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setLinkDetails(params?.row);
            setAnchorEl(event.currentTarget);
          }}
        >
          <KebabMenu2Icon />
        </IconButton>
      ),
    },
  ];

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data?.data || []}
        name="paymentLink"
        totalCount={data?.data?.totalCount || 0}
        emptyComponent={
          <NoData
            text="No Payment Links Sent Yet"
            subText="Once you send a payment link to a client, it will appear here with its status."
            customImg={
              <LinkIcon height={80} width={80} color="var(--neutral-100)" />
            }
          />
        }
        maxHeight={"calc(100vh - 326px)"}
      />

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              width: "170px",
              borderRadius: "8px",
              p: "8px 0",
              border: "1px solid var(--neutral-100)",
              bgcolor: "var(--white)",
              boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
            },
          },
        }}
      >
        <Grid2 container>
          <Grid2
            size={12}
            p={"12px"}
            display={"flex"}
            alignItems={"center"}
            gap={1}
            sx={{ cursor: "pointer" }}
          >
            <LinkIcon />
            <Typography
              fontSize={12}
              fontWeight={600}
              color="var(--grey-900)"
              onClick={() => {
                handleCopyLink(linkDetails?.linkUrl);
                setAnchorEl(null);
              }}
            >
              Copy Payment link
            </Typography>
          </Grid2>

          <Show.When isTrue={linkDetails?.linkStatus === "ACTIVE"}>
            <Grid2
              size={12}
              p={"12px"}
              display={"flex"}
              alignItems={"center"}
              gap={1}
              color={"var(--warning-700)"}
              sx={{ cursor: "pointer" }}
              onClick={() => {
                cancelPaymentLink.mutate(linkDetails.id);
                setAnchorEl(null);
              }}
            >
              <CloseIcon />
              <Typography fontSize={12} fontWeight={600}>
                Cancel Payment link
              </Typography>
            </Grid2>
          </Show.When>
        </Grid2>
      </Popover>
    </>
  );
};

export default PaymentLinkModal;
